import React, { lazy, Suspense } from 'react';
import UniversalRouter from 'universal-router';

import loggerGenerator from '@travel/logger';
import { parseSearchQuery } from '@travel/traveler-core/utils/jrdpHelper';
import { lazyWithSuspense, parseQueryString } from '@travel/utils';

import Loading from 'pages/HotelList/components/Loading';
import HotelListResolver from 'pages/HotelList/resolver';
import HotelListMapResolver from 'pages/HotelListMap/resolver';
import PlanDetailResolver from 'pages/PlanDetail/resolver';
import PlansListResolver from 'pages/PlansList/resolver';
import SkeletonPage from 'pages/SkeletonPage';
import TopPageResolver from 'pages/Top/resolver';

import paths from 'constants/paths';
import App from 'core/app';
import AppResolver from 'core/resolver';
import { normalizePathIdentifier } from 'utils/search';

import type { universalRouterProps } from './types';
const logger = loggerGenerator();

const TopPage = lazyWithSuspense(() => import(/* webpackChunkName: "pages/Top" */ 'pages/Top'), {
  fallback: <SkeletonPage />,
});
const HotelList = lazy(() => import(/* webpackChunkName: "pages/HotelList" */ 'pages/HotelList'));
const HotelListMap = lazyWithSuspense(
  () => import(/* webpackChunkName: "pages/HotelListMap" */ 'pages/HotelListMap'),
  { fallback: <SkeletonPage /> },
);
const PlansList = lazyWithSuspense(
  () => import(/* webpackChunkName: "pages/PlansList" */ 'pages/PlansList'),
  { fallback: <SkeletonPage /> },
);
const PlanDetail = lazyWithSuspense(
  () => import(/* webpackChunkName: "pages/PlanDetail" */ 'pages/PlanDetail'),
  { fallback: <SkeletonPage /> },
);
const ErrorPage = lazyWithSuspense(
  () => import(/* webpackChunkName: "pages/ErrorPage" */ 'pages/ErrorPage'),
  {
    fallback: <SkeletonPage />,
  },
);

export default (props: universalRouterProps) => {
  const isServer = props.isServer || false;

  const routes = [
    {
      path: '/',
      async action(context: any) {
        let children;

        try {
          children = await context.next();
        } catch (e) {
          logger.error(e);
          children = null;
        }

        if (isServer) {
          await AppResolver(props);
        } else if (!context.skipResolver) {
          AppResolver(props);
        }

        return (
          <App {...context} key="app">
            {children}
          </App>
        );
      },
      children: [
        {
          path: paths.top.path,
          chunkName: 'pages/Top',
          async action(context: any) {
            if (isServer) {
              await TopPageResolver(props);
            } else if (!context.skipResolver) {
              TopPageResolver(props);
            }

            return <TopPage {...context} />;
          },
        },
        {
          path: paths.providerList.path,
          chunkName: 'pages/HotelList',
          async action(context: any) {
            if (!context.params.pageIdentifier) {
              props.res?.status(400);

              return <ErrorPage />;
            }
            const { isTravelRakutenOrigin } = props.store.getState().router;
            const { normalizePath, pageNumber } = normalizePathIdentifier(
              context.params.pageIdentifier,
            );
            const params = {
              pageIdentifier: normalizePath,
              pageNumber,
            };

            if (isServer && !isTravelRakutenOrigin) {
              await HotelListResolver(props, params, context.query);
            } else if (!context.skipResolver || isTravelRakutenOrigin) {
              HotelListResolver(props, params, context.query);
            }

            const fallback = props.store.getState().hotelList.isFirstTimeLoading ? (
              <Loading />
            ) : (
              <SkeletonPage />
            );

            return (
              <Suspense fallback={fallback}>
                <HotelList {...context} params={params} />
              </Suspense>
            );
          },
        },

        {
          path: paths.providerListMap.path,
          chunkName: 'pages/HotelListMap',
          async action(context: any) {
            const { normalizePath, pageNumber } = normalizePathIdentifier(
              context.params.pageIdentifier,
            );

            const params = {
              pageIdentifier: normalizePath,
              pageNumber,
            };

            if (!isServer) {
              HotelListMapResolver(props, params, context.query);
            }

            return <HotelListMap {...context} params={params} />;
          },
        },

        {
          path: paths.plansList.path,
          chunkName: 'pages/PlansList',
          async action(context: any) {
            const { isTravelRakutenOrigin } = props.store.getState().router;

            const { pageNumber = 1 } = normalizePathIdentifier(
              context.params?.pageNum || context.params.pageIdentifier,
            );
            const params = {
              pageIdentifier: context.params.pageIdentifier,
              pageNumber,
            };

            if (isServer && !isTravelRakutenOrigin) {
              await PlansListResolver(props, params, context.query);
            } else if (!context.skipResolver || isTravelRakutenOrigin) {
              PlansListResolver(props, params, context.query);
            }

            return <PlansList {...context} params={params} />;
          },
        },
        {
          path: paths.planDetail.path,
          chunkName: 'pages/PlanDetail',
          async action(context: any) {
            if (isServer) {
              await PlanDetailResolver(props, context.params, context.query);
            } else if (!context.skipResolver) {
              PlanDetailResolver(props, context.params, context.query);
            }

            return <PlanDetail {...context} />;
          },
        },
        {
          // also resolve paths.notFound.path
          path: '(.*)',
          chunkName: 'pages/ErrorPage',
          async action() {
            props.res?.status(400);
            return <ErrorPage />;
          },
        },
      ],
    },
  ];
  return new UniversalRouter(routes, {
    resolveRoute(context, params) {
      if (typeof context.route.action === 'function') {
        if (context.search) {
          const searchQuery = (context.search as string).replace(/\?$/, '');

          const defaultQuery = parseQueryString(searchQuery);
          context.query = { ...defaultQuery, ...parseSearchQuery(defaultQuery) };
        }

        if (props.initialChunks && context.route.chunkName) {
          props.initialChunks.push(context.route.chunkName);
        }

        return context.route.action(context, params);
      }
      return undefined;
    },
  });
};
